import React, { useEffect, useState } from "react";
import useLogin from "../hooks/useLogin";
import { checkIfAuthenticated } from "../utils/utils";
import { LOAN, LOGIN } from "../config/apiConfig";
import { GoAlertFill } from "react-icons/go";
import {
  Alert,
  Button,
  FileInput,
  Label,
  Modal,
  Progress,
  TextInput,
} from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { useForm } from "react-hook-form";
import Input from "../components/inputs/Input";
import CNPJInput from "../components/inputs/CNPJInput";
import PhoneInput from "../components/inputs/PhoneInput";
import { GiFarmTractor, GiReceiveMoney } from "react-icons/gi";
import { TbReportMoney } from "react-icons/tb";
import TitleStripe from "../components/common/TitleStripe";
import CurrencyInput from "../components/inputs/CurrencyInput";
import SelectInput from "../components/inputs/SelectInput";
import { useQuery, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { useParams } from "react-router-dom";
import DrawerLoading from "../components/DrawerLoading";
import FileUploader from "../components/inputs/FileUploader";

const FormPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(true);
  const [accessKeyInput, setAccessKeyInput] = useState("");
  const [accessKeyError, setAccessKeyError] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const {
    register,
    getValues,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
  } = useForm();

  const {
    isLoading,
    error: errorLoan,
    data: loan,
    refetch,
  } = useQuery(
    ["loan"],
    async () => {
      const { data } = await axios.get(
        `${LOAN.GET_LOAN_BY_IDENTIFIER}/${id}?accessKey=${accessKeyInput}`
      );

      setValue("company.name", data?.data?.Company?.name);
      setValue("company.document", data?.data?.Company?.document);
      setValue("company.responsibleName", data?.data?.Company?.responsibleName);
      setValue("company.phoneNumber", data?.data?.Company?.phoneNumber);
      setValue("company.email", data?.data?.Company?.email);
      setValue("company.cep", data?.data?.Company?.cep);
      setValue("company.address", data?.data?.Company?.address);
      setValue("company.addressNumber", data?.data?.Company?.addressNumber);
      setValue(
        "company.addressComplement",
        data?.data?.Company?.addressComplement
      );
      setValue(
        "company.addressNeighborhood",
        data?.data?.Company?.addressNeighborhood
      );
      setValue("company.addressCity", data?.data?.Company?.addressCity);
      setValue("credit.segmentTypeId", data?.data?.segmentTypeId);
      setValue("credit.amount", data?.data?.amount);
      setValue("credit.installments", data?.data?.installments);

      return data.data;
    },
    {
      enabled: false,
    }
  );

  const { isLoading: isLoadingSegments, data: segmentsData } = useQuery(
    ["segments"],
    async () => {
      const { data } = await axios.get(LOAN.GET_SEGMENTS);
      return data.data;
    }
  );

  useEffect(() => {
    if (
      loan?.segmentTypeId &&
      segmentsData?.length > 0 &&
      !watch("credit.category")
    ) {
      const foundSegment = segmentsData.find((segment) =>
        segment.SegmentTypes?.some((type) => type.id === loan.segmentTypeId)
      );

      if (foundSegment) {
        setValue("credit.category", foundSegment.id);
      }
    }
  }, [loan, segmentsData]);

  const checkAccessKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.get(
        `${LOAN.GET_LOAN_BY_IDENTIFIER}/${id}?accessKey=${accessKeyInput}`
      );

      if (data?.data) {
        setShowAccessKeyModal(false);
        setAccessKeyError(false);
        refetch();
      }
    } catch (err) {
      setAccessKeyError(true);
    }
  };

  const uploadedDocs = loan?.Documents || [];
  const isUploaded = (documentTypeId, partnerId = null) => {
    return uploadedDocs?.some(
      (doc) =>
        doc.documentTypeId === documentTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );
  };

  const countUploadedDocs = (appliesTo, partnerId = null) => {
    const total = documentTypes?.filter(
      (doc) => doc.appliesTo === appliesTo || doc.appliesTo === "both"
    )?.length;

    const uploaded = uploadedDocs?.filter(
      (doc) =>
        (doc.partnerId ?? null) === (partnerId ?? null) &&
        documentTypes?.some(
          (type) =>
            type.id === doc.documentTypeId &&
            (type.appliesTo === appliesTo || type.appliesTo === "both")
        )
    )?.length;

    return { total, uploaded };
  };

  const savePartialForm = async () => {
    try {
      setErrorSubmit(false);
      setIsSubmitting(true);
      const data = getValues();
      await axios.post(`${LOAN.UPLOAD_PARTIAL_FORM}/${id}`, data);
    } catch (error) {
      setErrorSubmit(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    isLoading: isLoadingDocumentTypes,
    error: errorDocumentTypes,
    data: documentTypes,
  } = useQuery(["documentTypes"], async () => {
    const { data } = await axios.get(`${LOAN.GET_DOCUMENT_TYPES}`);

    return data.data;
  });

  const onSubmitCompany = async (data) => {
    try {
      await savePartialForm();
      setActiveStep(2);
    } catch (error) {
    } finally {
    }
  };

  const onSubmitCredit = async (data) => {
    try {
      await savePartialForm();
      setActiveStep(3);
    } catch (error) {
    } finally {
    }
  };

  const areAllDocumentsUploaded = () => {
    const companyDocsOk = documentTypes
      ?.filter(
        (item) => item.appliesTo === "company" || item.appliesTo === "both"
      )
      ?.every((item) => isUploaded(item.id));

    const partnersDocsOk = loan?.Company?.Partners?.every((partner) => {
      return documentTypes
        ?.filter(
          (item) => item.appliesTo === "partner" || item.appliesTo === "both"
        )
        ?.every((item) => isUploaded(item.id, partner.id));
    });

    return companyDocsOk && partnersDocsOk;
  };

  const onSubmitDocuments = async (data) => {
    setActiveStep(4);
  };

  const getRejectionReason = (documentTypeId, partnerId = null) => {
    const doc = uploadedDocs
      ?.filter(
        (doc) =>
          doc.documentTypeId === documentTypeId &&
          (doc.partnerId ?? null) === (partnerId ?? null)
      )
      ?.find((doc) => doc.rejectionReason); // pega o que tem rejectionReason preenchido

    return doc?.rejectionReason || null;
  };

  return (
    <section
      className="flex flex-col justify-center items-center bg-black form-sec fixed left-0 bottom-0 right-0 top-0"
      style={{ height: "100vh", backgroundImage: "url('/assets/bg_form.jpg')" }}
    >
      {!showAccessKeyModal && (
        <div className="z-10 flex flex-col items-center justify-center w-full">
          <div className="text-white font-semibold mb-5 mt-4 text-lg md:text-2xl hidden md:block">
            Formulário de simulação de crédito
          </div>
          <div className="max-w-4xl bg-white w-full p-5 rounded-none md:rounded-md h-full md:h-auto relative">
            {(isLoading || isLoadingDocumentTypes) && <DrawerLoading />}
            <ol className="flex items-center w-full text-sm font-medium text-center text-gray-500 dark:text-gray-400 sm:text-base">
              <li className="flex md:w-full items-center text-primary dark:text-blue-500 sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
                <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                  {activeStep >= 1 ? (
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5"
                      aria-hidden="true"
                      xmlns="https://www.w3.org/2000/svg"
                      fill="var(--primary)"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                    </svg>
                  ) : (
                    <span className="me-2">1</span>
                  )}
                  <span className="hidden md:block">Empresa</span>
                </span>
              </li>
              <li className="flex md:w-full items-center after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
                <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                  {activeStep >= 2 ? (
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5"
                      aria-hidden="true"
                      xmlns="https://www.w3.org/2000/svg"
                      fill="var(--primary)"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                    </svg>
                  ) : (
                    <span className="me-2">2</span>
                  )}
                  <span className="hidden md:block">Crédito</span>
                </span>
              </li>
              <li className="flex md:w-full items-center after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
                <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                  {activeStep >= 3 ? (
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5"
                      aria-hidden="true"
                      xmlns="https://www.w3.org/2000/svg"
                      fill="var(--primary)"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                    </svg>
                  ) : (
                    <span className="me-2">3</span>
                  )}
                  <span className="hidden md:block">Documentos</span>
                </span>
              </li>
              <li className="flex items-center">
                {activeStep === 4 ? (
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2.5"
                    aria-hidden="true"
                    xmlns="https://www.w3.org/2000/svg"
                    fill="var(--primary)"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
                  </svg>
                ) : (
                  <span className="me-2">4</span>
                )}
                <span className="hidden md:block">Confirmação</span>
              </li>
            </ol>

            {activeStep === 1 && (
              <form
                className="mt-5 form-amplia form-doc overflow-y-auto"
                onSubmit={handleSubmit(onSubmitCompany)}
              >
                <div className="text-gray-500 italic text-sm">
                  Confirme os dados da empresa:
                </div>
                <div className="title-syncx mt-3">Informações da empresa</div>

                <div
                  className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 relative"
                  style={{ zIndex: 1 }}
                >
                  <CNPJInput
                    register={register}
                    name="company.document"
                    label="CNPJ"
                    control={control}
                  />
                  <Input
                    register={register}
                    name="company.name"
                    label="Razão Social"
                  />
                </div>
                <div
                  className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 relative"
                  style={{ zIndex: 1 }}
                >
                  <Input
                    register={register}
                    name="company.responsibleName"
                    label="Nome do responsável"
                  />
                  <PhoneInput
                    control={control}
                    name="company.phoneNumber"
                    label="Telefone com DDD"
                    error={errors?.company?.phoneNumber}
                  />
                  <Input
                    register={register}
                    type="email"
                    name="company.email"
                    label="E-mail"
                  />
                </div>
                <div className="title-syncx mt-4">Endereço da empresa</div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input register={register} name="company.cep" label="CEP" />
                  <Input
                    register={register}
                    name="company.address"
                    label="Endereço"
                  />
                  <Input
                    register={register}
                    name="company.addressNumber"
                    label="Número"
                  />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    register={register}
                    name="company.addressComplement"
                    label="Complemento"
                  />
                  <Input
                    register={register}
                    name="company.addressNeighborhood"
                    label="Bairro"
                  />
                  <Input
                    register={register}
                    name="company.addressCity"
                    label="Cidade"
                  />
                </div>
                <div className="flex justify-center mt-5 gap-2 btn-form-amplia">
                  <Button
                    type="submit"
                    className="px-6 py-1"
                    isProcessing={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Salvando..." : "Próximo"}
                  </Button>
                </div>
              </form>
            )}

            {activeStep === 2 && (
              <form
                className="mt-5 form-amplia form-doc overflow-y-auto"
                onSubmit={handleSubmit(onSubmitCredit)}
              >
                <div className="title-syncx">Informações do crédito</div>
                <div className="mt-5 font-semibold underline">
                  Selecione o segmento
                </div>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  {segmentsData?.map((segment) => (
                    <button
                      key={segment.id}
                      className={`border border-gray-300 rounded-md p-5 flex flex-col items-center justify-center ${
                        watch("credit.category") == segment.id
                          ? "bg-gray-500 text-white"
                          : "border-dashed"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setValue("credit.category", segment.id);
                        setValue("credit.segmentTypeId", null); // reseta tipo
                      }}
                    >
                      {segment.name === "Capital de Giro" ? (
                        <TbReportMoney fontSize={25} />
                      ) : (
                        <GiFarmTractor fontSize={25} />
                      )}
                      <div>{segment.name}</div>
                    </button>
                  ))}
                </div>

                {watch("credit.category") && (
                  <div>
                    <div className="font-semibold underline mb-2 mt-5">
                      Selecione uma das opções abaixo:
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                      {segmentsData
                        ?.find((seg) => seg.id === watch("credit.category"))
                        ?.SegmentTypes?.map((type) => (
                          <div
                            key={type.id}
                            className={`border border-gray-200 rounded-md shadow-sm p-2 text-gray-600 cursor-pointer ${
                              watch("credit.segmentTypeId") == type.id
                                ? "bg-gray-500 text-white"
                                : ""
                            }`}
                            onClick={() =>
                              setValue("credit.segmentTypeId", type.id)
                            }
                          >
                            {type.name}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div className="font-semibold underline mt-5 mb-4">
                  Quanto deseja emprestar?
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CurrencyInput
                    register={register}
                    label="Quanto deseja emprestar?"
                    control={control}
                    name="credit.amount"
                  />
                  <SelectInput
                    register={register}
                    label="Em quantas parcelas?"
                    name="credit.installments"
                  >
                    {Array.from({ length: 20 })?.map((_, index) => (
                      <option key={index} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className="flex justify-center mt-5 gap-2 btn-form-amplia">
                  <Button
                    className="px-6 py-1 bg-gray-500"
                    onClick={() => setActiveStep(1)}
                    disabled={isSubmitting}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="px-6 py-1"
                    isProcessing={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Salvando..." : "Próximo"}
                  </Button>
                </div>
              </form>
            )}

            {activeStep === 3 && (
              <form
                className="mt-5 overflow-y-auto form-doc form-amplia"
                onSubmit={handleSubmit(onSubmitDocuments)}
              >
                <div className="title-syncx">Documentos necessários</div>
                <TitleStripe>
                  Documentos da empresa ({countUploadedDocs("company").uploaded}
                  /{countUploadedDocs("company").total})
                </TitleStripe>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                  {documentTypes
                    ?.filter(
                      (item) =>
                        item.appliesTo === "company" ||
                        item.appliesTo === "both"
                    )
                    ?.map((item) => (
                      <div key={item.id}>
                        <FileUploader
                          id={`file-${item.id}`}
                          label={item.name}
                          uploaded={isUploaded(item.id)}
                          onUpload={async (file) => {
                            const formData = new FormData();
                            formData.append("file", file);
                            formData.append("documentTypeId", item.id);
                            formData.append("documentName", item.name);

                            await axios.post(
                              `${LOAN.UPLOAD_LOAN_DOCUMENT}/${id}`,
                              formData,
                              {
                                headers: {
                                  "Content-Type": "multipart/form-data",
                                },
                              }
                            );

                            queryClient.setQueryData(["loan"], (old) => {
                              if (!old) return old;

                              const updatedDocuments = old.Documents.map(
                                (doc) => {
                                  if (doc.documentTypeId === item.id) {
                                    return {
                                      ...doc,
                                      rejectionReason: null, // limpa o motivo de rejeição
                                    };
                                  }
                                  return doc;
                                }
                              );

                              return {
                                ...old,
                                Documents: updatedDocuments,
                              };
                            });

                            queryClient.invalidateQueries(["loan"]);

                            queryClient.invalidateQueries(["loan"]);
                          }}
                          rejectionReason={getRejectionReason(item.id) || null}
                        />
                      </div>
                    ))}
                </div>
                <TitleStripe>Documentos dos sócios</TitleStripe>
                {loan?.Company?.Partners?.map((partner) => (
                  <div key={partner.id} className="mb-10">
                    <div className="font-semibold capitalize mb-2 mt-4">
                      Documentos: {partner.name?.toLowerCase()} (
                      {countUploadedDocs("partner", partner.id).uploaded}/
                      {countUploadedDocs("partner", partner.id).total})
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                      {documentTypes
                        ?.filter(
                          (item) =>
                            item.appliesTo === "partner" ||
                            item.appliesTo === "both"
                        )
                        ?.map((item) => (
                          <FileUploader
                            key={`${item.id}-p-${partner.id}`}
                            id={`file-${item.id}-partner-${partner.id}`}
                            label={item.name}
                            uploaded={isUploaded(item.id, partner.id)} // ajustado abaixo
                            onUpload={async (file) => {
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("documentTypeId", item.id);
                              formData.append("partnerId", partner.id);
                              formData.append("documentName", item.name);

                              await axios.post(
                                `${LOAN.UPLOAD_LOAN_DOCUMENT}/${id}`,
                                formData,
                                {
                                  headers: {
                                    "Content-Type": "multipart/form-data",
                                  },
                                }
                              );

                              queryClient.setQueryData(["loan"], (old) => {
                                if (!old) return old;

                                const updatedDocuments = old.Documents.map(
                                  (doc) => {
                                    if (
                                      doc.documentTypeId === item.id &&
                                      (doc.partnerId ?? null) ===
                                        (partner?.id ?? null)
                                    ) {
                                      return {
                                        ...doc,
                                        rejectionReason: null, // limpa o motivo de rejeição
                                      };
                                    }
                                    return doc;
                                  }
                                );

                                return {
                                  ...old,
                                  Documents: updatedDocuments,
                                };
                              });

                              queryClient.invalidateQueries(["loan"]);
                            }}
                            rejectionReason={
                              getRejectionReason(item.id, partner.id) || null
                            }
                          />
                        ))}
                    </div>
                  </div>
                ))}

                <div className="flex justify-center mt-5 gap-2 btn-form-amplia">
                  <Button
                    className="px-6 py-1 bg-gray-500"
                    onClick={() => setActiveStep(2)}
                    disabled={isSubmitting}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="px-6 py-1"
                    isProcessing={isSubmitting}
                    disabled={isSubmitting || !areAllDocumentsUploaded()}
                  >
                    {isSubmitting ? "Salvando..." : "Próximo"}
                  </Button>
                </div>
              </form>
            )}

            {activeStep === 4 && (
              <form
                className="mt-5 form-amplia form-doc overflow-y-auto"
                onSubmit={handleSubmit(onSubmitCredit)}
              >
                <div className="title-syncx">Formulário de SCR Bacen</div>

                <div className="mt-5 bg-gray-50 border border-gray-300 rounded p-4 text-sm md:text-base text-gray-700">
                  <p className="mb-3">
                    Para finalizar sua solicitação de crédito, é necessário
                    autorizar a consulta ao
                    <strong>
                      {" "}
                      SCR (Sistema de Informações de Crédito) do Bacen
                    </strong>
                    .
                  </p>
                  <p>
                    Clique no botão abaixo para acessar a autorização e assinar
                    digitalmente o termo. Esse processo é seguro e obrigatório
                    para seguirmos com a análise.
                  </p>

                  <div className="mt-4 text-center">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-3 px-4 rounded"
                    >
                      Iniciar processo de assinatura do formulário
                    </a>
                  </div>
                </div>

                <div className="flex justify-center mt-5 gap-2 btn-form-amplia">
                  <Button
                    className="px-6 py-1 bg-gray-500"
                    onClick={() => setActiveStep(3)}
                    disabled={isSubmitting}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="px-6 py-1"
                    isProcessing={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Salvando..." : "Finalizar"}
                  </Button>
                </div>
              </form>
            )}
          </div>
          <img
            className="w-32 mt-6 hidden md:block"
            src="/assets/logo-white.png"
          />
        </div>
      )}

      <Modal
        show={showAccessKeyModal}
        size="md"
        onClose={() => {}}
        dismissible={false}
      >
        <Modal.Body>
          <form onSubmit={checkAccessKey}>
            <div className="space-y-4">
              <p className="text-sm text-gray-800 text-center">
                Para continuar com o preenchimento do formulário, digite a senha
                de acesso fornecida pela nossa equipe.
              </p>
              <TextInput
                type="password"
                placeholder="Senha de acesso"
                value={accessKeyInput}
                onChange={(e) => setAccessKeyInput(e.target.value)}
                color={accessKeyError ? "failure" : "gray"}
                helperText={
                  accessKeyError ? (
                    <span className="text-red-600 text-xs -mt-2 block">
                      Senha incorreta. Tente novamente.
                    </span>
                  ) : null
                }
              />
            </div>
            <Button className="mx-auto mt-4 px-6 py-1" type="submit">
              Acessar
            </Button>
          </form>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default FormPage;
